/* eslint-disable no-param-reassign */
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
// core
import { Button, Checkbox, FormControlLabel } from '@material-ui/core';
// icons
import CheckBox from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlank from '@material-ui/icons/CheckBoxOutlineBlank';

import useStyles from './styles';

import Modal from '../../components/Modal';
import Select from '../../components/Select';
import Snackbar from '../../components/Snackbar';
import { MULTIPLE_SELECTION } from '../../utils/const/pageTypes';

// En la edición, las alternativas borradas siguen en `questionChoices` con
// `deleted: true` hasta guardar, para que el API las dé de baja. No llevan lógica.
const getActiveChoices = (question) => (question.questionChoices || [])
  .filter((choice) => !choice.deleted);

const isEmptyChoice = (choiceIdentifier) => [null, undefined, ''].includes(choiceIdentifier);

/**
 * Agrega filas vacías para las alternativas que no tienen fila de lógica (por
 * ejemplo, una alternativa agregada después de configurar las conexiones). Sin
 * ellas el modal no muestra dónde asignarles el salto y el guardado se bloquea.
 *
 * @param {object} question Pregunta con la lógica abierta.
 * @returns {object} La misma pregunta si no falta ninguna fila, o una copia completada.
 */
const addMissingChoiceRows = (question) => {
  const questionLogic = question.questionLogic || [];
  const assignedChoices = questionLogic.map((logic) => `${logic.questionChoiceIdentifier}`);
  const uncoveredChoices = getActiveChoices(question)
    .filter((choice) => !assignedChoices.includes(`${choice.identifier}`)).length;
  const emptyRows = questionLogic
    .filter((logic) => isEmptyChoice(logic.questionChoiceIdentifier)).length;
  const missingRows = uncoveredChoices - emptyRows;

  if (missingRows <= 0) return question;

  return {
    ...question,
    questionLogic: [
      ...questionLogic,
      ...Array.from({ length: missingRows }, () => ({
        questionIdentifier: `${question.identifier}`,
        questionChoiceIdentifier: null,
        nextQuestionIdentifier: '',
      })),
    ],
  };
};

function QuestionLogic({
  open, setOpen, inputValues, setInputValues, view,
}) {
  const styles = useStyles();
  const { t } = useTranslation();

  const [logicOpen, setLogicOpen] = useState([]);
  const [otherCases, setOtherCases] = useState({});
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('error');
  const [isSaving, setIsSaving] = useState(false);
  const pendingUpdates = useRef(0);

  const handleSave = async () => {
    setIsSaving(true);
    // Wait for pending updates
    await new Promise((resolve) => {
      const checkPending = () => {
        if (pendingUpdates.current === 0) {
          resolve();
        } else {
          setTimeout(checkPending, 50);
        }
      };
      checkPending();
    });

    // Check if there are any questions with options without logic
    const hasUnassignedOptions = inputValues.some((question) => {
      if (!logicOpen.includes(`${question.identifier}`)) return false;
      if (!question.questionLogic) return false;
      const choiceIdentifiers = getActiveChoices(question).map(
        (choice) => choice.identifier.toString(),
      );
      const logicIdentifiers = question.questionLogic.map(
        (logic) => logic.questionChoiceIdentifier,
      );
      return choiceIdentifiers.some((choiceId) => !logicIdentifiers.includes(choiceId));
    });

    if (hasUnassignedOptions) {
      setMessage(t('task.questionLogic.unassignedOptionsAlert'));
      setSeverity('error');
      setIsSaving(false);
      return;
    }

    setOpen(false);
    setIsSaving(false);
  };

  const handleCheckOtherCases = (event) => {
    const { name } = event.target;

    const questionNumber = name.split('-')[0];
    const choiceNumber = name.split('-')[1];

    if (questionNumber in otherCases) {
      const newOtherCases = { ...otherCases };
      delete newOtherCases[questionNumber];
      setOtherCases(newOtherCases);
      return;
    }

    const newOtherCases = { ...otherCases };
    newOtherCases[questionNumber] = {
      limit: choiceNumber,
    };

    setOtherCases(newOtherCases);
  };

  const handleAddLogic = (event) => {
    pendingUpdates.current += 1;
    const newInputValues = [...inputValues];
    const { id } = event.currentTarget;

    const index = newInputValues.findIndex((question) => `${question.identifier}` === id);
    if (index === -1) {
      pendingUpdates.current -= 1;
      return;
    }
    if (!newInputValues[index].questionLogic) newInputValues[index].questionLogic = [];
    const totalChoices = getActiveChoices(newInputValues[index]).length;
    for (let i = 0; i < totalChoices; i += 1) {
      if (newInputValues[index].questionLogic.length >= totalChoices) break;
      newInputValues[index].questionLogic.push({
        questionIdentifier: id,
        questionChoiceIdentifier: null,
        nextQuestionIdentifier: '',
      });
    }

    const exists = logicOpen.includes(id);

    if (exists) {
      pendingUpdates.current -= 1;
      return;
    }

    setInputValues(newInputValues);
    setLogicOpen([...logicOpen, id]);
    setTimeout(() => { pendingUpdates.current -= 1; }, 0);
  };

  const handleRemoveAll = () => {
    pendingUpdates.current += 1;
    const newInputValues = [...inputValues];
    for (let i = 0; i < newInputValues.length; i += 1) {
      const questionIdentifier = newInputValues[i].identifier;
      newInputValues[i].questionLogic = [
        {
          questionIdentifier,
          questionChoiceIdentifier: null,
          nextQuestionIdentifier: (parseInt(questionIdentifier, 10) + 1).toString(),
        },
      ];
    }
    setInputValues(newInputValues);
    setLogicOpen([]);
    setTimeout(() => { pendingUpdates.current -= 1; }, 0);
  };

  const handleDeleteLogic = (event) => {
    pendingUpdates.current += 1;
    const newInputValues = [...inputValues];
    const { id } = event.currentTarget;
    const qIndex = newInputValues.findIndex((question) => `${question.identifier}` === id);
    if (qIndex === -1) {
      pendingUpdates.current -= 1;
      return;
    }
    newInputValues[qIndex].questionLogic = [
      {
        questionIdentifier: id,
        questionChoiceIdentifier: null,
        nextQuestionIdentifier: (parseInt(id, 10) + 1).toString(),
      },
    ];

    const oldLogicOpen = [...logicOpen];
    const newLogicOpen = oldLogicOpen.filter((l) => l !== id);
    setLogicOpen(newLogicOpen);
    setInputValues(newInputValues);
    setTimeout(() => { pendingUpdates.current -= 1; }, 0);
  };

  const handleSelectQuestionLogic = useCallback((event) => {
    pendingUpdates.current += 1;
    const { name, value } = event.target;
    const [questionId, qLogicIndex, noMoreCases] = name.split('-');

    setInputValues((prev) => {
      const qIndex = prev.findIndex((q) => `${q.identifier}` === questionId);
      if (qIndex === -1) {
        setTimeout(() => { pendingUpdates.current -= 1; }, 0);
        return prev;
      }

      const updated = [...prev];
      updated[qIndex] = { ...updated[qIndex], questionLogic: [...updated[qIndex].questionLogic] };

      if (noMoreCases) {
        updated[qIndex].questionLogic.forEach((ql, index) => {
          if (index > qLogicIndex) {
            ql.questionIdentifier = questionId;
            ql.questionChoiceIdentifier = null;
            ql.nextQuestionIdentifier = value;
          }
        });
      } else {
        updated[qIndex].questionLogic[qLogicIndex] = {
          ...updated[qIndex].questionLogic[qLogicIndex],
          nextQuestionIdentifier: value,
        };
      }
      setTimeout(() => { pendingUpdates.current -= 1; }, 0);
      return updated;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectQuestionLogicOtherCases = (event) => {
    pendingUpdates.current += 1;
    const newInputValues = [...inputValues];
    const { name, value } = event.target;
    const [questionId, qLogicIndex, noMoreCases] = name.split('-');

    const qIndex = newInputValues.findIndex(
      (question) => `${question.identifier}` === questionId,
    );

    if (qIndex === -1) {
      pendingUpdates.current -= 1;
      return;
    }

    if (noMoreCases) {
      // Filter question logics that already have logic
      const filteredQuestionLogics = newInputValues[qIndex].questionLogic
        .filter((ql) => [null, ''].includes(ql.questionChoiceIdentifier));

      // Filter question choices that are being used in question logics
      const usedQuestionChoiceIdentifiers = newInputValues[qIndex].questionLogic.map(
        (ql) => (ql.questionChoiceIdentifier
          ? ql.questionChoiceIdentifier.toString() : null),
      );
      const filteredChoices = getActiveChoices(newInputValues[qIndex])
        .filter((qc) => !usedQuestionChoiceIdentifiers.includes(qc.identifier.toString()));

      // For each question choice, make sure a question logic exists with correct next question
      filteredChoices.forEach((questionChoice, index) => {
        if (index < filteredQuestionLogics.length) {
          filteredQuestionLogics[index].questionIdentifier = questionId;
          filteredQuestionLogics[index]
            .questionChoiceIdentifier = questionChoice.identifier.toString();
          filteredQuestionLogics[index].nextQuestionIdentifier = value;
        }
      });
    } else if (newInputValues[qIndex].questionLogic?.[qLogicIndex]) {
      newInputValues[qIndex].questionLogic[qLogicIndex].nextQuestionIdentifier = value;
    }

    setInputValues(newInputValues);
    setTimeout(() => { pendingUpdates.current -= 1; }, 0);
  };

  const handleSelectQuestionChoice = useCallback((event) => {
    pendingUpdates.current += 1;
    const { name, value } = event.target;
    const [id, index] = name.split('-');

    setInputValues((prev) => {
      const i = prev.findIndex((q) => q.identifier === id);
      if (i === -1) {
        setTimeout(() => { pendingUpdates.current -= 1; }, 0);
        return prev;
      }

      const updated = [...prev];
      updated[i] = { ...updated[i], questionLogic: [...updated[i].questionLogic] };
      updated[i].questionLogic[index] = {
        ...updated[i].questionLogic[index],
        questionChoiceIdentifier: value,
      };
      setTimeout(() => { pendingUpdates.current -= 1; }, 0);
      return updated;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const questionOptionsCache = useMemo(() => {
    const cache = {};
    inputValues.forEach((q) => {
      cache[q.identifier] = inputValues
        .filter((opt) => `${opt.identifier}` !== `${q.identifier}`)
        .map((opt) => ({ name: `${opt.identifier} ${opt.title}`, value: opt.identifier }));
      cache[q.identifier].unshift({ name: t('task.questionLogic.select'), value: '' });
      cache[q.identifier].push({ name: t('task.questionLogic.end'), value: 'end' });
    });
    return cache;
  }, [inputValues, t]);

  const questionChoiceOptionsCache = useMemo(() => {
    const cache = {};
    inputValues.forEach((q) => {
      const choices = getActiveChoices(q);
      if (choices.length) {
        cache[q.identifier] = choices.map((qc) => ({
          name: qc.title,
          value: qc.identifier.toString(),
        }));
        cache[q.identifier].unshift({ name: t('task.questionLogic.select'), value: '' });
      } else {
        cache[q.identifier] = [];
      }
    });
    return cache;
  }, [inputValues, t]);

  useEffect(() => {
    const logic = inputValues.map((question) => question.questionLogic || []);
    const logicOpenIds = inputValues.map((question) => question.identifier);
    logic.forEach((questionLogic, index) => {
      if (questionLogic.length > 1) {
        setLogicOpen((prevState) => [...prevState, logicOpenIds[index]]);
      }
    });

    const completeOpenLogic = (question) => (
      (question.questionLogic || []).length > 1 ? addMissingChoiceRows(question) : question
    );
    if (inputValues.some((question) => completeOpenLogic(question) !== question)) {
      setInputValues((prev) => prev.map(completeOpenLogic));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      title={t('task.questionLogic.modalTitle')}
      defaultOpen={open}
      setCurrentOpen={setOpen}
    >
      <div className={styles.questionLogicContainer}>
        {inputValues.map((question) => (
          <div
            className={styles.questionContainer}
            key={question.identifier}
          >
            <div className={styles.questionAndButton}>
              <div className={styles.question}>
                <div className={styles.questionIdentifier}>
                  {question.identifier}
                </div>
                <p>
                  {question.title}
                </p>
              </div>
              {!view && (logicOpen.includes(question.identifier) ? (
                <Button
                  className={styles.closeAddLogicButton}
                  onClick={handleDeleteLogic}
                  id={question.identifier}
                >
                  Eliminar lógica
                </Button>
              ) : (
                <Button
                  id={question.identifier}
                  className={styles.addLogicButton}
                  onClick={handleAddLogic}
                  disabled={getActiveChoices(question).length === 0
                    || question.type === MULTIPLE_SELECTION
                  }
                >
                  {t('task.questionLogic.addLogicButton')}
                </Button>
              ))}
            </div>
            {logicOpen.includes(question.identifier) ? (
              (question.questionLogic || [])
                .slice(0, question.identifier.toString() in otherCases
                  ? otherCases[question.identifier].limit
                  : (question.questionLogic || []).length)
                .map((questionLogic, index) => (
                  <div key={`${question.identifier}-${index}`}>
                    <div className={styles.detailConditions}>
                      <Select
                        name="conditional"
                        items={[{ name: 'Si...', value: 'if' }]}
                        value="if"
                        onChange={() => { }}
                        fullWidth
                      />
                      <div>
                        <Select
                          name="condition"
                          items={[{ name: 'es', value: 'equal' }]}
                          value="equal"
                          onChange={() => { }}
                          fullWidth
                        />
                        <Select
                          name={`${question.identifier}-${index}`}
                          items={questionChoiceOptionsCache[question.identifier] || []}
                          value={questionLogic.questionChoiceIdentifier || ''}
                          onChange={handleSelectQuestionChoice}
                          fullWidth
                        />
                      </div>
                    </div>
                    <div className={styles.detailConditions}>
                      <p>{t('task.questionLogic.then')}</p>
                      <div>
                        <Select
                          name="jumpTo"
                          items={[{ name: 'Saltar a', value: 'jumpTo' }]}
                          value="jumpTo"
                          onChange={() => { }}
                          width="100px"
                        />
                        <Select
                          name={`${question.identifier}-${index}`}
                          items={questionOptionsCache[question.identifier] || []}
                          value={Number.parseInt(questionLogic.nextQuestionIdentifier, 10)
                            >= inputValues.length + 1
                            || !questionLogic.nextQuestionIdentifier
                            ? '' : questionLogic.nextQuestionIdentifier}
                          onChange={handleSelectQuestionLogic}
                          fullWidth
                        />
                      </div>
                    </div>
                    {question.questionLogic.length - 1 === index ? null : (
                      <FormControlLabel
                        className={styles.checkbox}
                        control={
                          <Checkbox
                            name={`${question.identifier}-${index + 1}`}
                            icon={<CheckBoxOutlineBlank />}
                            checkedIcon={<CheckBox />}
                            onChange={handleCheckOtherCases}
                            checked={
                              otherCases[question.identifier]
                              && otherCases[question.identifier].limit === (index + 1).toString()
                            }
                          />
                        }
                        label="El resto de las alternativas conducen a la misma pregunta"
                      />
                    )}
                    {question.identifier.toString() in otherCases ? (
                      <div className={styles.detailConditions}>
                        <p>{t('task.questionLogic.otherCases')}<b>{t('task.questionLogic.jumpTo')}</b></p>
                        <Select
                          name={`${question.identifier}-${index}-noMoreCases`}
                          items={questionOptionsCache[question.identifier] || []}
                          value={Number.parseInt(
                            question.questionLogic?.[question.questionLogic.length - 1]
                              ?.nextQuestionIdentifier, 10,
                          )
                            >= inputValues.length + 1
                            || !question.questionLogic?.[question.questionLogic.length - 1]
                              ?.nextQuestionIdentifier
                            ? '' : question.questionLogic[question.questionLogic.length - 1].nextQuestionIdentifier}
                          onChange={handleSelectQuestionLogicOtherCases}
                          fullWidth
                        />
                      </div>
                    ) : null}
                  </div>))
            ) : (
              <div className={styles.detailLogic}>
                <p>
                  {t('task.questionLogic.alwaysJumpTo')}
                </p>
                <Select
                  name={`${question.identifier}-0`}
                  items={questionOptionsCache[question.identifier] || []}
                  value={Number.parseInt(
                    question.questionLogic?.[0]?.nextQuestionIdentifier, 10,
                  )
                    >= inputValues.length + 1
                    || !question.questionLogic?.[0]?.nextQuestionIdentifier
                    ? '' : question.questionLogic[0].nextQuestionIdentifier}
                  onChange={handleSelectQuestionLogic}
                  width="250px"
                />
              </div>
            )}
          </div>
        ))}
        {!view && <div className={styles.buttonsContainer}>
          <Button onClick={handleRemoveAll} disabled={isSaving}>
            {t('task.questionLogic.removeAllButton')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {t('task.questionLogic.saveButton')}
          </Button>
        </div>}
      </div>
      <div>
        <Snackbar
          open={message !== ''}
          message={message}
          severity={severity}
          onClose={() => setMessage('')}
        />
      </div>
    </Modal>
  );
}

export default QuestionLogic;
